<template>
  <div>
    <div class="date-text-field-container">
      <v-text-field
        v-model="dateRangeText"
        prepend-icon="mdi-calendar"
        readonly
        class="date-text-field"
      ></v-text-field>
      <v-icon
        aria-hidden="false"
        aria-label="Reset date range"
        title="Reset date range"
        class="primary--text text--lighten-1 cursor-pointer"
        :disabled="!hasChanged"
        @click="resetDateRange"
      >
        mdi-undo
      </v-icon>
    </div>
    <p v-if="message" class="warning-message">{{ message }}</p>
    <v-date-picker
      v-model="range"
      range
      full-width
      color="primary"
      class="date-picker"
      @change="handleOnChange"
      :show-current="utcDate"
    ></v-date-picker>
  </div>
</template>

<script>
import lodashIsEqual from "lodash/isEqual";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

import {
  nowUtc,
  getFormattedDate,
  getTodayStartEndDate,
  isToday,
} from "@/common/date.utilities";
import { mapGetters } from "vuex";
import { MODULE_PREFIX_TIME } from "@/store";

const EMITTED_ACTIONS = {
  CHANGE: "change",
};

export default {
  name: "SearchFilterDateRangePicker",
  props: {
    dateRangeStartDaysBack: {
      type: Number,
    },
    dateRangeMaxDateBack: {
      type: String,
    },
    reset: {
      type: String,
    },
    selectedRange: {
      type: Array,
    },
  },
  data() {
    return {
      min: -60,
      max: 0,
      range: this.getDefaultRangeDates(),
      message: null,
      hasChanged: false,
    };
  },
  computed: {
    ...mapGetters({
      utcDate: MODULE_PREFIX_TIME + "today",
    }),
    dateRangeText() {
      const dateStart = this.range[0]
        ? getFormattedDate(
            this.range[1]
              ? this.range[0] < this.range[1]
                ? this.range[0]
                : this.range[1]
              : this.range[0]
          )
        : null;
      const dateEnd = this.range[1]
        ? getFormattedDate(
            this.range[1] > this.range[0] ? this.range[1] : this.range[0]
          )
        : null;
      const dates = [];
      if (dateStart) {
        dates.push(dateStart);
      }
      if (dateEnd) {
        dates.push(dateEnd);
      }
      return dates.join(" to ");
    },
  },
  methods: {
    handleOnChange() {
      const startDate =
        this.range[0] < this.range[1] ? this.range[0] : this.range[1];
      const endDate =
        this.range[1] > this.range[0] ? this.range[1] : this.range[0];
      const start = dayjs.utc(startDate).diff(nowUtc(), "day");
      let end = 0;
      if (!isToday(endDate)) {
        end = dayjs.utc(endDate).diff(nowUtc(), "day");
      }
      const range = [start, end];
      this.$emit(EMITTED_ACTIONS.CHANGE, {
        range: range,
        start: getTodayStartEndDate(startDate).start,
        end: getTodayStartEndDate(endDate).end,
      });
    },
    getDefaultRangeDates() {
      return this.getRangeDates([-1 * this.dateRangeStartDaysBack, 0]);
    },
    getRangeDates(range) {
      const start = dayjs
        .utc(nowUtc())
        .subtract(Math.abs(range[0]), "day")
        .format("YYYY-MM-DD");
      let end = dayjs.utc(nowUtc()).format("YYYY-MM-DD");
      if (range[1] !== 0) {
        end = dayjs
          .utc(nowUtc())
          .subtract(Math.abs(range[1]), "day")
          .format("YYYY-MM-DD");
      }
      if (end < start) {
        return [end, start];
      } else {
        return [start, end];
      }
    },
    resetDateRange() {
      this.range = this.getDefaultRangeDates();
      this.handleOnChange();
      this.hasChanged = false;
    },
  },
  watch: {
    reset: {
      handler() {
        this.resetDateRange();
      },
    },
    dateRangeMaxDateBack: {
      handler(newValue, oldValue) {
        if (oldValue !== newValue) {
          this.min = -60;
          if (this.dateRangeMaxDateBack) {
            this.min = dayjs
              .utc(this.dateRangeMaxDateBack)
              .diff(nowUtc(), "day");
          }
        }
      },
    },
    selectedRange: {
      handler(newVal, oldVal) {
        if (!lodashIsEqual(newVal, oldVal)) {
          if (
            this.selectedRange &&
            this.selectedRange.length > 1 &&
            this.selectedRange[0] < this.min &&
            this.selectedRange[1] < this.min
          ) {
            this.message =
              "Your search's dates exceed the minimum for Events AI. Only Verified and Analysis will be displayed.";
            this.hasChanged = true;
          } else if (!this.selectedRange) {
            //If no selected, set everything to default values
            this.message = null;
            this.resetDateRange();
          } else {
            this.message = null;

            this.range = this.getRangeDates(this.selectedRange);

            let defaults = this.getDefaultRangeDates();
            this.hasChanged =
              this.range[0] === defaults[0] && this.range[1] === defaults[1]
                ? false
                : true;

            this.handleOnChange();
          }
        }
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>

<style>
.date-picker .v-date-picker-title__date {
  font-size: 20px !important;
}
.date-text-field.v-text-field {
  padding-top: 0 !important;
}
.date-text-field-container .date-text-field input {
  width: 100%;
  font-size: 0.85em;
}
</style>

<style scoped>
.warning-message {
  color: red;
  font-size: 10px;
}
.date-text-field-container {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}
.date-text-field-container > .date-text-field {
  flex-grow: 2;
}
.date-text-field-container > button {
  flex-grow: 1;
  font-size: 1em;
}
</style>
